import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  useTheme,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate, useParams } from "react-router-dom";
import PermissionGroupService from "../../services/doctorServices/PermissionGroupService";
import PermissionMatrix from "../../components/PermissionMatrix";
import SharedFilesSelector from "../../components/SharedFilesSelector";
import Headeer from "../../components/Headeer";
import SecurityIcon from "@mui/icons-material/Security";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";

export default function CreatePermissionGroup() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('permissionGroups');
  const navigate = useNavigate();
  const { id } = useParams(); // Get group ID from URL for edit mode
  const permissionService = new PermissionGroupService();

  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    type: "doctor", // Fixed to doctor type
    name: "",
    label: "",
    description: "",
  });

  const [permissions, setPermissions] = useState({});
  const [sharedFiles, setSharedFiles] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success", autoHideDuration: 3000 });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch group data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchGroupData();
    }
  }, [id]);

  // Wrapper function to handle permission changes with synchronization
  const handlePermissionsChange = (newPermissions) => {
    // Get current and new values
    const currentInvitationsCreate = permissions.can_own_invitations_create;
    const currentPermissionGroupsCreate = permissions.can_own_permission_groups_create;
    const newInvitationsCreate = newPermissions.can_own_invitations_create;
    const newPermissionGroupsCreate = newPermissions.can_own_permission_groups_create;

    // Check if invitations create changed
    const invitationsChanged = currentInvitationsCreate !== newInvitationsCreate;
    // Check if permission groups create changed
    const permissionGroupsChanged = currentPermissionGroupsCreate !== newPermissionGroupsCreate;

    // If invitations create is being toggled
    if (invitationsChanged && !permissionGroupsChanged) {
      // Synchronize: if invitations create is set to true, set permission groups create to true
      // If invitations create is set to false, set permission groups create to false
      newPermissions.can_own_permission_groups_create = newInvitationsCreate === true;
    }
    // If permission groups create is being toggled
    else if (permissionGroupsChanged && !invitationsChanged) {
      // Synchronize: if permission groups create is set to true, set invitations create to true
      // If permission groups create is set to false, set invitations create to false
      newPermissions.can_own_invitations_create = newPermissionGroupsCreate === true;
    }

    setPermissions(newPermissions);
  };

  const fetchGroupData = async () => {
    try {
      setPageLoading(true);
      const group = await permissionService.getGroupById(id);
      
      // Set basic form data
      setFormData({
        type: group.type,
        name: group.name,
        label: group.label,
        description: group.description || "",
      });

      // Extract permissions (all can_* fields)
      const permissionData = {};
      Object.keys(group).forEach(key => {
        if (key.startsWith('can_')) {
          permissionData[key] = group[key];
        }
      });
      setPermissions(permissionData);

      // Extract shared_files if exists
      if (group.shared_files && Array.isArray(group.shared_files)) {
        setSharedFiles(group.shared_files);
      }
    } catch (error) {
      console.error("Error fetching group data:", error);
      setSnackbar({
        open: true,
        message: t('errorLoadingGroupData'),
        severity: "error",
      });
      setTimeout(() => {
        navigate("/doctor-permission-groups");
      }, 2000);
    } finally {
      setPageLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('groupNameRequired');
    } else if (formData.name.length < 3) {
      newErrors.name = t('groupNameMinLength');
    }

    if (!formData.label.trim()) {
      newErrors.label = t('displayLabelRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: t('pleaseFixFormErrors'),
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      
      const groupData = {
        ...formData,
        ...permissions,
      };

      // For doctor type, ensure can_own_doctors_view is always true (default permission)
      if (formData.type === 'doctor') {
        groupData.can_own_doctors_view = true;
      }

      // Add shared_files if any (only for doctor type)
      if (formData.type === 'doctor' && sharedFiles.length > 0) {
        // Clean sharedFiles - remove fileName (display only field)
        groupData.shared_files = sharedFiles.map(sf => {
          const { fileName, ...cleanSf } = sf;
          return cleanSf;
        });
      }

      if (isEditMode) {
        await permissionService.updateGroup(id, groupData);
        setSnackbar({
          open: true,
          message: t('groupUpdatedSuccessfully'),
          severity: "success",
        });
      } else {
        await permissionService.createGroup(groupData);
        setSnackbar({
          open: true,
          message: t('groupCreatedSuccessfully'),
          severity: "success",
        });
      }

      setTimeout(() => {
        navigate("/doctor-permission-groups");
      }, 2000);
    } catch (error) {
      if (error.response?.status === 403) {
        return;
      }
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        (isEditMode ? t("errorUpdatingGroup") : t("errorCreatingGroup"));
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedPermissionsCount = Object.values(permissions).filter(v => v === true).length;

  // Show loading state while fetching data in edit mode
  if (pageLoading) {
    return (
      <Box m="20px">
        <Headeer title={t('loading')} subtitle={t('pleaseWait')} />
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={50} sx={{ color: colors.blueAccent[500], mb: 2 }} />
            <Typography variant="h5" sx={{ color: colors.grey[300] }}>
              {t('loadingGroupData')}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={() => navigate("/doctor-permission-groups")}
          sx={{
            backgroundColor: `${colors.grey[700]}40`,
            "&:hover": {
              backgroundColor: `${colors.grey[600]}60`,
            },
          }}
        >
          <ArrowBackIcon sx={{ color: colors.grey[300] }} />
        </IconButton>
        
        <Headeer 
          title={isEditMode ? t('editPermissionGroup') : t('createPermissionGroup')}
          subtitle={t('subtitle')}
        />
      </Box>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.grey[100],
              fontWeight: 700,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            📝 {t('basicInformation')}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Group Type - Fixed to Doctor */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: `${colors.greenAccent[800]}20`,
                border: `1px solid ${colors.greenAccent[500]}40`,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: colors.grey[300], mb: 1, fontWeight: 600 }}
              >
                {t('groupType')}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MedicalServicesIcon sx={{ fontSize: 22, color: "white" }} />
                </Box>
                <Typography sx={{ color: colors.grey[100], fontWeight: 700, fontSize: "1.1rem" }}>
                  {t('doctorType')}
                </Typography>
              </Box>
            </Box>

            {/* Group Name */}
            <TextField
              fullWidth
              label={t('groupName')}
              placeholder={t('groupNamePlaceholder')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase().replace(/\s/g, '_') })}
              error={!!errors.name}
              helperText={
                isEditMode 
                  ? t('groupNameCannotChange')
                  : (errors.name || t('groupNameHelperText'))
              }
              required
              disabled={isEditMode}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: isEditMode ? colors.grey[700] : colors.primary[600],
                  "& fieldset": {
                    borderColor: `${colors.grey[800]}40`,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.blueAccent[500],
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.greenAccent[500],
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  color: colors.grey[100],
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[300],
                },
                "& .MuiFormHelperText-root": {
                  color: colors.grey[400],
                },
              }}
            />

            {/* Display Label */}
            <TextField
              fullWidth
              label={t('displayLabel')}
              placeholder={t('displayLabelPlaceholder')}
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              error={!!errors.label}
              helperText={errors.label || t('displayLabelHelperText')}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.primary[600],
                  "& fieldset": {
                    borderColor: `${colors.grey[800]}40`,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.blueAccent[500],
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.greenAccent[500],
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  color: colors.grey[100],
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[300],
                },
                "& .MuiFormHelperText-root": {
                  color: colors.grey[400],
                },
              }}
            />

            {/* Description */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('description')}
              placeholder={t('descriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.primary[600],
                  "& fieldset": {
                    borderColor: `${colors.grey[800]}40`,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.blueAccent[500],
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.greenAccent[500],
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  color: colors.grey[100],
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[300],
                },
                "& .MuiFormHelperText-root": {
                  color: colors.grey[400],
                },
              }}
            />
          </Box>
        </Paper>

        {/* Permission Matrix */}
        <Box sx={{ mb: 3 }}>
          {/* Info Alert about Invitations and Permission Groups */}
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: "12px",
              backgroundColor: `${colors.blueAccent[800]}30`,
              border: `1px solid ${colors.blueAccent[500]}40`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.blueAccent[300],
                lineHeight: 1.6,
                fontWeight: 500,
              }}
              dangerouslySetInnerHTML={{ __html: t('permissionsSyncNote') }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography
              variant="h5"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {t('permissionsConfiguration')}
            </Typography>
            <Chip
              label={t('permissionsSelected', { count: selectedPermissionsCount })}
              sx={{
                backgroundColor: selectedPermissionsCount > 0 
                  ? `${colors.greenAccent[700]}80`
                  : `${colors.grey[700]}`,
                color: selectedPermissionsCount > 0 
                  ? colors.greenAccent[100]
                  : colors.grey[300],
                fontWeight: 600,
                border: `1px solid ${selectedPermissionsCount > 0 
                  ? colors.greenAccent[500] 
                  : colors.grey[600]}40`,
              }}
            />
          </Box>

          <PermissionMatrix
            type={formData.type}
            permissions={permissions}
            onChange={handlePermissionsChange}
          />
        </Box>

        {/* Shared Files Section (only for doctor type) */}
        {formData.type === 'doctor' && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.grey[800]}30`,
              borderRadius: "16px",
            }}
          >
            <SharedFilesSelector
              sharedFiles={sharedFiles}
              onChange={setSharedFiles}
            />
          </Paper>
        )}

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/doctor-permission-groups")}
            disabled={loading}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: colors.grey[600],
              color: colors.grey[200],
              "&:hover": {
                borderColor: colors.grey[500],
                backgroundColor: `${colors.grey[700]}40`,
              },
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<SaveIcon />}
            sx={{
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
              },
              "&:disabled": {
                background: colors.grey[600],
                color: colors.grey[400],
              },
              transition: "all 0.3s ease",
            }}
          >
            {loading 
              ? (isEditMode ? t('updating') : t('creating')) 
              : t('saveChanges')}
          </Button>
        </Box>
      </form>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration || 3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: "100%",
            fontSize: snackbar.severity === "warning" ? "0.95rem" : "1rem",
            "& .MuiAlert-message": {
              fontWeight: snackbar.severity === "warning" ? 600 : 500,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}




